'use client'

import {createWithEqualityFn} from 'zustand/traditional'
import {devtools, persist, subscribeWithSelector} from 'zustand/middleware'
import {shallow} from 'zustand/shallow'
import {ShopifyCustomerProps, shopifyCustomerStore} from './slices/customerSlice'
import {ShopifyCustomerAuthProps, shopifyCustomerAuthStore} from './slices/authSlice'
import {ShopifyCustomerAddressProps, shopifyCustomerAddressStore} from './slices/addressSlice'
import {ShopifyCartProps, shopifyCartStore} from './slices/cartSlice'
import {clientLocale} from '@local/stores/contexts/LocaleContextProvider'
import {GetOptions, get as fetcher} from '@local/utils/fetch'

export const fetchWithLocale = (url, options?: Omit<GetOptions, 'next'>) => fetcher(url, {locale: clientLocale.current, ...options})
export const deleteWithLocale = (url, body, options?: Omit<GetOptions, 'next'>) => fetchWithLocale(url, {method: 'DELETE', body, ...options})
export const postWithLocale = (url, body, options?: Omit<GetOptions, 'next'>) => fetchWithLocale(url, {method: 'POST', body, ...options})
export const patchWithLocale = (url, body, options?: Omit<GetOptions, 'next'>) => fetchWithLocale(url, {method: 'PATCH', body, ...options})
export const putWithLocale = (url, body, options?: Omit<GetOptions, 'next'>) => fetchWithLocale(url, {method: 'PUT', body, ...options})

interface ShopifyStoreProps extends ShopifyCustomerProps, ShopifyCartProps, ShopifyCustomerAddressProps, ShopifyCustomerAuthProps {
	init: () => void
}

export const useShopifyStore = createWithEqualityFn<ShopifyStoreProps>()(
	persist(
		subscribeWithSelector(
			(set, get, store) => ({
				...shopifyCustomerStore(set, get, store),
				...shopifyCustomerAuthStore(set, get, store),
				...shopifyCustomerAddressStore(set, get, store),
				...shopifyCartStore(set, get, store),

				init: async () => {
					await get().initCustomer()
					await get().initCart()
				},
			}),
		),
		{
			name: 'shop-store', // unique name
			partialize: ({customerAccessToken, cartId}) => ({customerAccessToken, cartId}),
			onRehydrateStorage: () => {
				return (state, error) => {
					if (error) {
						console.warn('packages/shopify/hooks/useShopifyStore.ts | an error happened during shop-store hydration', error)
					} else {
						state?.init()
					}
				}
			},
		},
	),
	shallow,
)

// if ('BroadcastChannel' in globalThis) {
// 	share('customerAccessToken', shopifyStore)
// 	share('cartId', shopifyStore)
// }

if (process.env.NODE_ENV === 'development') {
	devtools(useShopifyStore)
}
